// Generatore ESC/POS per termiche 80mm (42 colonne Font A) — es. AXON MICRELEC POS80K
const ESC = "\x1b";
const GS = "\x1d";
const COLS = 42;

export const CMD = {
  INIT: ESC + "@",
  CENTER: ESC + "a\x01",
  LEFT: ESC + "a\x00",
  RIGHT: ESC + "a\x02",
  BOLD_ON: ESC + "E\x01",
  BOLD_OFF: ESC + "E\x00",
  DOUBLE_ON: GS + "!\x11",
  DOUBLE_OFF: GS + "!\x00",
  CUT: "\n\n\n\n" + GS + "V\x42\x00",
};

const eur = (n) => Number(n || 0).toFixed(2).replace(".", ",") + " EUR";
const rule = (ch = "-") => ch.repeat(COLS) + "\n";
const lr = (l, r, cols = COLS) => {
  l = String(l); r = String(r);
  if (l.length + r.length + 1 > cols) l = l.slice(0, cols - r.length - 2) + "…";
  return l + " ".repeat(Math.max(1, cols - l.length - r.length)) + r + "\n";
};
const centerLines = (text) => String(text || "").split("\n").map((t) => t.trim()).filter(Boolean).join("\n") + "\n";
const PAY_LABEL = { cash: "CONTANTI", card: "CARTA", ticket: "TICKET", mixed: "MULTIPLO", assegno: "ASSEGNO", bonifico: "BONIFICO", non_riscosso: "NON RISCOSSO", omaggio: "OMAGGIO" };

const SIZE_CMD = { small: ESC + "M\x01", normal: ESC + "M\x00", large: GS + "!\x11" };
const RESET_STYLE = ESC + "M\x00" + GS + "!\x00" + ESC + "E\x00";
// Righe formattate {text, size: small|normal|large, bold} → ESC/POS
const styledLines = (lines) =>
  (Array.isArray(lines) ? lines : [])
    .map((l) => {
      let s = SIZE_CMD[l.size] || SIZE_CMD.normal;
      if (l.bold) s += CMD.BOLD_ON;
      s += (l.text || "") + "\n" + RESET_STYLE;
      return s;
    })
    .join("");

// Totale riga con sconti riga (% poi €), extras esclusi
export const lineTotal = (l) => {
  if (!l || typeof l !== "object") return 0;
  const price = Number(l.price) || 0;
  const qty = Number(l.qty) || 0;
  const pct = Number(l.discount_pct) || 0;
  const eurOff = Number(l.discount_eur) || 0;
  return Math.max(0, price * qty * (1 - pct / 100) - eurOff);
};

// ===== Layout stampa configurabile (posizione + grandezza + righe vuote) =====
export const DEFAULT_PRINT_LAYOUT = {
  receipt: {
    date: { align: "left", size: "normal" },
    items: { align: "left", size: "normal" },
    operator: { align: "left", size: "normal" },
    spacing: { after_header: 0, after_date: 0, after_items: 0, after_total: 0 },
  },
  comanda: {
    date: { align: "center", size: "normal" },
    items: { align: "left", size: "large" },
    operator: { align: "left", size: "normal" },
    spacing: { after_header: 0, after_date: 0, after_items: 0 },
  },
};
export const layoutOf = (tenant, doc, elem) => ({
  ...DEFAULT_PRINT_LAYOUT[doc][elem],
  ...(((tenant || {}).print_layout || {})[doc]?.[elem] || {}),
});
export const spacingOf = (tenant, doc) => ({
  ...DEFAULT_PRINT_LAYOUT[doc].spacing,
  ...(((tenant || {}).print_layout || {})[doc]?.spacing || {}),
});
const blanks = (n) => "\n".repeat(Math.max(0, Math.min(9, Number(n) || 0)));

const ALIGN_CMD = { left: CMD.LEFT, center: CMD.CENTER, right: CMD.RIGHT };
const sizeOn = (size) => (size === "large" ? CMD.DOUBLE_ON : size === "small" ? SIZE_CMD.small : "");
const colsFor = (size) => (size === "large" ? 21 : COLS);
// Riga di testo libero con allineamento + dimensione
const styledLine = (st, text) => ALIGN_CMD[st.align] + sizeOn(st.size) + text + "\n" + RESET_STYLE + CMD.LEFT;
// Riga sinistra/destra: se align=left resta a colonne giustificate, altrimenti testo unico allineato
const styledLR = (st, l, r) => {
  if ((st.align || "left") === "left") return sizeOn(st.size) + lr(l, r, colsFor(st.size)) + RESET_STYLE;
  return styledLine(st, `${l}  ${r}`);
};

export const buildReceiptEscpos = ({ tenant = {}, order = {}, kind = "preconto" }) => {
  const isManagement = kind === "management";
  const isPreconto = kind === "preconto";
  const usePrecontoBrand = isPreconto || isManagement;
  const isFiscalTest = kind === "fiscal_test";
  const sp = spacingOf(tenant, "receipt");
  let out = CMD.INIT + CMD.CENTER;

  const headerLines = usePrecontoBrand && Array.isArray(tenant.preconto_header_lines) && tenant.preconto_header_lines.length
    ? tenant.preconto_header_lines
    : null;
  const headerText = usePrecontoBrand && !headerLines && tenant.preconto_header ? tenant.preconto_header : null;
  if (headerLines) {
    out += styledLines(headerLines);
  } else if (headerText) {
    out += CMD.BOLD_ON + centerLines(headerText) + CMD.BOLD_OFF;
  }
  // Intestazione personalizzata preconto/gestionale SOSTITUISCE i dati aziendali (no doppia intestazione)
  if (!headerLines && !headerText) {
    if (!usePrecontoBrand && tenant.receipt_header) out += CMD.BOLD_ON + centerLines(tenant.receipt_header) + CMD.BOLD_OFF;
    out += CMD.DOUBLE_ON + CMD.BOLD_ON + (tenant.business_name || "VELTA SISTEMI") + "\n" + CMD.BOLD_OFF + CMD.DOUBLE_OFF;
    if (tenant.address) out += tenant.address + "\n";
    out += "P.IVA " + (tenant.vat_number || "-") + "\n";
    if (tenant.phone) out += "Tel. " + tenant.phone + "\n";
  }
  out += blanks(sp.after_header);

  if (isFiscalTest) {
    out += rule("=") + CMD.BOLD_ON + "STAMPA DI PROVA\nNON VALIDO AI FINI FISCALI\n" + CMD.BOLD_OFF + rule("=");
  }

  out += CMD.LEFT + rule();
  if (isFiscalTest && order.receipt_number) {
    out += lr("Scontrino N.", "#" + String(order.receipt_number).padStart(4, "0"));
  }
  const d = new Date(order.closed_at || order.created_at || Date.now());
  const dateStr = d.toLocaleDateString("it-IT") + " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (!usePrecontoBrand) {
    out += styledLR(layoutOf(tenant, "receipt", "date"), "Data", dateStr);
    out += rule();
  }
  if (order.order_type === "refund") {
    out += CMD.CENTER + CMD.BOLD_ON + (order.refund_reason === "annullamento" ? "EMESSO PER ANNULLAMENTO\n" : "EMESSO PER RESO MERCE\n") + CMD.BOLD_OFF + CMD.LEFT;
    if (order.refund_number) out += lr("Documento N.", "S-" + order.refund_number);
    if (order.original_receipt_number) out += lr("Rif. scontrino", "#" + order.original_receipt_number);
  }
  out += blanks(sp.after_date);

  const itemsLay = layoutOf(tenant, "receipt", "items");
  (order.lines || []).forEach((l) => {
    const gross = l.price * l.qty;
    out += styledLR(itemsLay, `${l.qty}x ${l.name}`, eur(gross));
    const net = lineTotal(l);
    if (net < gross - 0.001) {
      out += lr(l.discount_pct ? `   Sconto -${l.discount_pct}%` : "   Sconto", "-" + eur(gross - net));
    }
    (l.extras || []).forEach((ex) => { out += lr(`  + ${ex.name}`, ex.price ? eur(ex.price) : "gratis"); });
    if (l.note) out += CMD.BOLD_ON + `  >> ${l.note}\n` + CMD.BOLD_OFF;
  });
  if (order.notes) out += CMD.BOLD_ON + "NOTA: " + order.notes + "\n" + CMD.BOLD_OFF;
  out += blanks(sp.after_items);
  out += rule();

  const orderPct = Number(order.discount_pct || 0);
  const orderEur = Number(order.discount_eur || 0);
  if (orderPct > 0 || orderEur > 0) {
    const grossSum = (order.lines || []).reduce(
      (s, l) => s + lineTotal(l) + (l.extras || []).reduce((a, ex) => a + Number(ex.price || 0), 0), 0);
    out += lr("Subtotale", eur(grossSum));
    if (orderPct > 0) out += lr(`Sconto -${orderPct}%`, "-" + eur(grossSum * orderPct / 100));
    if (orderEur > 0) out += lr("Sconto", "-" + eur(Math.min(orderEur, grossSum * (1 - orderPct / 100))));
  }
  out += CMD.DOUBLE_ON + CMD.BOLD_ON + lr("TOTALE", eur(order.total), 21) + CMD.BOLD_OFF + CMD.DOUBLE_OFF;
  if (order.payment_method) out += lr("Pagamento", PAY_LABEL[order.payment_method] || order.payment_method);
  const bk = order.payment_breakdown || {};
  Object.keys(bk).filter((k) => Number(bk[k]) > 0).forEach((k) => {
    out += lr("  " + (PAY_LABEL[k] || k), eur(bk[k]));
  });
  if (order.cash_tendered) {
    out += lr("Contanti ricevuti", eur(order.cash_tendered));
    if (order.change_due != null) {
      out += CMD.DOUBLE_ON + CMD.BOLD_ON + lr("RESTO", eur(order.change_due), 21) + CMD.BOLD_OFF + CMD.DOUBLE_OFF;
    }
  }
  if (order.lottery_code) out += CMD.CENTER + "Lotteria: " + order.lottery_code + "\n" + CMD.LEFT;
  if (order.operator_name) out += styledLine(layoutOf(tenant, "receipt", "operator"), "Operatore: " + order.operator_name);
  out += blanks(sp.after_total);
  if (usePrecontoBrand) {
    const dl = layoutOf(tenant, "receipt", "date");
    const p4 = (n) => String(n).padStart(4, "0");
    const ref = (n) => (order.z_number != null ? `${p4(order.z_number)}-${p4(n)}` : p4(n));
    if (isManagement && order.management_number != null) out += styledLine(dl, `DOC.GESTIONALE NR. ${ref(order.management_number)}`);
    if (isPreconto && order.preconto_number != null) out += styledLine(dl, `DOC. NR. ${ref(order.preconto_number)}`);
    const ed = new Date(order.closed_at || Date.now());
    const p2 = (n) => String(n).padStart(2, "0");
    out += styledLine(dl, `Emesso il ${p2(ed.getDate())}/${p2(ed.getMonth() + 1)}/${ed.getFullYear()} ${p2(ed.getHours())}:${p2(ed.getMinutes())}`);
  }

  out += rule() + CMD.CENTER;
  if (isManagement) {
    // Movimento gestionale: NESSUNA scritta PRECONTO
    out += "DOCUMENTO NON FISCALE\n";
    if (Array.isArray(tenant.preconto_footer_lines) && tenant.preconto_footer_lines.length) {
      out += styledLines(tenant.preconto_footer_lines);
    } else {
      const footer = tenant.preconto_footer || tenant.receipt_footer;
      if (footer) out += centerLines(footer);
    }
  } else if (isPreconto) {
    out += CMD.BOLD_ON + "PRECONTO\n" + CMD.BOLD_OFF + "Documento non fiscale\n";
    if (Array.isArray(tenant.preconto_footer_lines) && tenant.preconto_footer_lines.length) {
      out += styledLines(tenant.preconto_footer_lines);
    } else {
      const footer = tenant.preconto_footer || tenant.receipt_footer;
      if (footer) out += centerLines(footer);
    }
  } else if (isFiscalTest) {
    out += "DOCUMENTO COMMERCIALE\nDI VENDITA O PRESTAZIONE\n";
    if (tenant.receipt_footer) out += centerLines(tenant.receipt_footer);
  } else {
    out += "DOCUMENTO NON FISCALE\n";
    if (tenant.receipt_footer) out += centerLines(tenant.receipt_footer);
  }
  if (tenant.receipt_thank_you) out += CMD.BOLD_ON + centerLines(tenant.receipt_thank_you) + CMD.BOLD_OFF;
  out += "Powered by VELTA SISTEMI\n" + CMD.CUT;
  return out;
};

// Copia non fiscale di una Lettura X / Chiusura Z da stampare su preconto o comanda
export const buildClosureEscpos = ({ tenant = {}, closure = {} }) => {
  const isZ = closure.kind === "z";
  const dt = new Intl.DateTimeFormat("it-IT", {
    timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(new Date(closure.created_at || Date.now()));
  let out = CMD.INIT + CMD.CENTER;
  out += CMD.BOLD_ON + (tenant.business_name || "VELTA SISTEMI") + "\n" + CMD.BOLD_OFF;
  out += CMD.DOUBLE_ON + CMD.BOLD_ON + (isZ ? "CHIUSURA Z" : "LETTURA X") + "\n" + CMD.BOLD_OFF + CMD.DOUBLE_OFF;
  out += dt + "\n" + CMD.LEFT + rule("=");
  out += lr("Documenti emessi", String(closure.count ?? 0));
  const pays = Object.entries(closure.by_payment || {});
  if (pays.length) {
    out += rule();
    pays.forEach(([k, v]) => { out += lr(PAY_LABEL[k] || String(k).toUpperCase(), eur(v)); });
  }
  const vats = Object.entries(closure.by_vat || {});
  if (vats.length) {
    out += rule();
    vats.forEach(([r, v]) => { out += lr(`IVA ${r}%`, eur(v)); });
  }
  out += rule("=");
  if (isZ) {
    out += lr("Incasso chiuso", eur(closure.total));
    out += rule();
    out += CMD.CENTER + CMD.BOLD_ON + "IMPORTO AZZERATO\n" + CMD.BOLD_OFF + CMD.LEFT;
    out += CMD.BOLD_ON + CMD.DOUBLE_ON + lr("TOTALE", eur(0), 21) + CMD.DOUBLE_OFF + CMD.BOLD_OFF;
    out += CMD.CENTER + "Progressivo preconto da 1\n" + CMD.LEFT;
  } else {
    out += CMD.BOLD_ON + CMD.DOUBLE_ON + lr("TOTALE", eur(closure.total), 21) + CMD.DOUBLE_OFF + CMD.BOLD_OFF;
  }
  out += "\n" + CMD.CENTER + "COPIA NON FISCALE\n" + CMD.LEFT + CMD.CUT;
  return out;
};

const COURSE_ESC = {
  "1": "TURNO 1",
  "2": "TURNO 2",
  "3": "TURNO 3",
  antipasti: "ANTIPASTI",
  primi: "PRIMI",
  secondi: "SECONDI",
};

const stationHeading = (station) => {
  const s = String(station || "").toLowerCase();
  if (s === "kitchen" || s.includes("cucina")) return "CUCINA";
  if (s === "pizzeria" || s.includes("pizza")) return "PIZZERIA";
  if (s === "bar") return "BAR";
  return String(station || "COMANDA").toUpperCase();
};

export const buildComandaEscpos = ({ station = "kitchen", tableName, roomName, orderNumber, lines = [], notes, operator, tenant = {} }) => {
  const stationLabel = stationHeading(station);
  const sp = spacingOf(tenant, "comanda");
  let out = CMD.INIT + CMD.CENTER;
  out += CMD.DOUBLE_ON + CMD.BOLD_ON + "COMANDA " + stationLabel + "\n" + CMD.BOLD_OFF + CMD.DOUBLE_OFF;
  if (roomName) out += CMD.BOLD_ON + "SALA " + String(roomName).toUpperCase() + "\n" + CMD.BOLD_OFF;
  if (tableName) out += CMD.DOUBLE_ON + "TAVOLO " + tableName + "\n" + CMD.DOUBLE_OFF;
  else if (orderNumber) out += CMD.DOUBLE_ON + "ORDINE N. " + orderNumber + "\n" + CMD.DOUBLE_OFF;
  if (operator) out += CMD.BOLD_ON + "CAMERIERE " + String(operator).toUpperCase() + "\n" + CMD.BOLD_OFF;
  out += blanks(sp.after_header);
  const d = new Date();
  const dateStr = d.toLocaleDateString("it-IT") + " " + d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  out += styledLine(layoutOf(tenant, "comanda", "date"), dateStr);
  out += blanks(sp.after_date);
  if (tenant.business_name) out += CMD.CENTER + tenant.business_name + "\n";
  out += CMD.LEFT + rule("=");
  const itemsLay = layoutOf(tenant, "comanda", "items");
  const hasCourse = (Array.isArray(lines) ? lines : []).some((l) => l.course);
  const ordered = hasCourse
    ? [...lines].sort((a, b) => String(a.course || "zzz").localeCompare(String(b.course || "zzz")))
    : lines;
  let lastCourse = null;
  ordered.forEach((l) => {
    if (hasCourse && (l.course || "") !== lastCourse) {
      lastCourse = l.course || "";
      const cl = COURSE_ESC[l.course] || (l.course ? String(l.course).toUpperCase() : "SENZA TURNO");
      out += rule("-") + CMD.BOLD_ON + CMD.DOUBLE_ON + cl + "\n" + CMD.DOUBLE_OFF + CMD.BOLD_OFF;
    }
    out += styledLine(itemsLay, `${l.qty}x ${l.name}`);
    (l.extras || []).forEach((ex) => { out += `   + ${ex.name}\n`; });
    if (l.note) out += CMD.BOLD_ON + `   >> ${String(l.note).toUpperCase()}\n` + CMD.BOLD_OFF;
  });
  if (notes) out += rule() + CMD.BOLD_ON + "NOTE: " + String(notes).toUpperCase() + "\n" + CMD.BOLD_OFF;
  out += blanks(sp.after_items);
  out += rule("=") + CMD.CUT;
  return out;
};
